import { useState } from 'react'
import { showToast } from '@/components/app/Toast/showToast'
import CopyIcon from '@/assets/icons/icon-copy.svg?react'
import CheckIcon from '@/assets/icons/icon-check.svg?react'
import css from './CopyField.module.css'

interface CopyFieldProps {
    value: string
    // Monospace text for keys/tokens (wraps); default is a single-line truncated link.
    mono?: boolean
    // Toast shown on copy. Omit for no toast.
    toastMessage?: string
}

// Read-only field with the copy affordance inside it: the whole field copies on click,
// and the trailing icon flips to a black check for ~2s as confirmation.
// Reuse this for any "copy this value" UI (share links, API keys, IDs, etc.).
const CopyField = ({ value, mono = false, toastMessage }: CopyFieldProps) => {
    const [copied, setCopied] = useState(false)

    const handleCopy = async () => {
        if (!value) return
        try {
            await navigator.clipboard.writeText(value)
            setCopied(true)
            if (toastMessage) showToast(toastMessage)
            window.setTimeout(() => setCopied(false), 2000)
        } catch {
            // Clipboard can be unavailable in some browsers — the value stays visible to copy manually.
        }
    }

    const textClassName = mono ? `${css.text} ${css.mono}` : `${css.text} ${css.truncate}`
    const iconClassName = copied ? `${css.icon} ${css.icon_copied}` : css.icon
    const Icon = copied ? CheckIcon : CopyIcon

    return (
        <button type="button" className={css.field} onClick={handleCopy} title={copied ? 'Copied' : 'Copy'}>
            <span className={textClassName}>{value}</span>
            <span className={css.icon_box}>
                <Icon className={iconClassName} />
            </span>
        </button>
    )
}

export default CopyField
